import React from "react";
import { Alert, Form, Icon, Input, Button, Row, Col } from "antd";
import { connect } from "react-redux";
import get from "lodash/get";

import { resetPassword, loginTwoFactor } from "../../reducers/user/actions";
import {
  getUserToken,
  getUnblindingRequestCreatePermission,
} from "../../reducers/user/selectors";
import {
  updateUnblindingRequest,
  getUnblindingRequest,
} from "../../api/requests";

import classes from "./PatientProfileForm.css";
import { LoadingOutlined } from "@ant-design/icons";
import { withNamespaces } from "react-i18next";

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some((field) => fieldsError[field]);
}

class PatientRegistryForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      getUnblindingLoading: false,
      unblinding: null,
      responseMessage: null,
      errorMessage: null,
      isSubmitted: false,
      checkConfirmRequestError: "",
      username: "",
    };
  }

  componentDidMount() {
    this.getUnblinding();
  }

  getUnblinding = async () => {
    this.setState({
      getUnblindingLoading: true,
    });

    try {
      const { data } = await getUnblindingRequest(this.props.subjectID);

      this.setState({
        unblinding: data,
      });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      this.setState({
        isLoading: false,
        requestResult: {
          type: "error",
          message: get(e, "response.data.message", this.props.i18n.t("Error")),
        },
      });
    } finally {
      this.setState({
        getUnblindingLoading: false,
      });
    }
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      this.setState({ isSubmitted: true });

      if (!err) {
        this.setState({
          isLoading: true,
          errorMessage: null,
          responseMessage: null,
        });

        try {
          const registry = { ...values };

          await updateUnblindingRequest(this.props.subjectID, registry);

          this.setState({ responseMessage: this.props.i18n.t('Success') });

          setTimeout(() => {
            this.props.onSubmit(this.props.subjectID, true);
          }, 1500);
        } catch (e) {
          if (get(e, "response.status") === 401) {
            this.props.logout();
            return;
          }

          this.setState({
            errorMessage: get(
              e,
              "response.data.msg",
              get(e, "response.data.error")
            ),
            isLoading: false,
          });

          if (get(e, "response.data.errors")) {
            const errors = {};
            Object.keys(get(e, "response.data.errors")).forEach(
              (errorFieldKey) => {
                errors[errorFieldKey] = {
                  value: values[errorFieldKey],
                  errors: get(e, `response.data.errors.${errorFieldKey}`).map(
                    (errorMessage) => new Error(errorMessage)
                  ),
                };
              }
            );
            this.props.form.setFields(errors);
          }
        }
      }
    });
  };

  render() {
    const { getFieldDecorator, getFieldsError } = this.props.form;
    const {
      isLoading,
      errorMessage,
      responseMessage,
      checkConfirmRequestError,
      getUnblindingLoading,
      unblinding,
    } = this.state;

    if (getUnblindingLoading) {
      return <LoadingOutlined />;
    }

    if (get(unblinding, "approved") === false) {
      return <p>{this.props.i18n.t('NamespacePatientRegistryForm:The unblinding request is waiting for approval.')}</p>;
    }

    if (get(unblinding, "approved") === true) {
      return (
        <p>
          <b>{this.props.i18n.t('NamespacePatientRegistryForm:Unblinding request details')}:</b> <i>{get(unblinding, "comments")}</i>
          <br/>
          <b>{this.props.i18n.t('NamespacePatientRegistryForm:The subject was randomized to')}:</b> <i>{get(unblinding, "drug_type_blinded")}</i>
        </p>
      );
    }

    return (
      <div className={classes.patientProfileForm}>
        <Form onSubmit={this.handleSubmit}>
          <Row gutter={8}>
            <Col sm={6}>
              <label>
                <b>{this.props.i18n.t('NamespacePatientRegistryForm:Please enter unblinding request details*')}</b>
                <Form.Item>
                  {getFieldDecorator("comments", {
                    rules: [
                      {
                        required: true,
                      },
                    ],
                    initialValue: get(this.props, "comments", ""),
                  })(<Input.TextArea disabled={!this.props.unblindingRequestCreatePermission} />)}
                </Form.Item>
              </label>
            </Col>
          </Row>
          {this.props.unblindingRequestCreatePermission && <Row gutter={8}>
            <Col sm={6}>
              <label>
                <b>{this.props.i18n.t('Password*')}</b>
                <Form.Item>
                  {getFieldDecorator("password", {
                    rules: [
                      {
                        required: true,
                      },
                    ],
                  })(<Input.Password />)}
                </Form.Item>
              </label>
            </Col>
          </Row>}

          {errorMessage && (
            <Alert
              message={errorMessage}
              type="error"
              className={classes.alertsMb}
            />
          )}
          {responseMessage && (
            <Alert
              message={responseMessage}
              type="success"
              className={classes.alertsMb}
            />
          )}
          {checkConfirmRequestError && (
            <Alert
              message={checkConfirmRequestError}
              type="error"
              className={classes.alertsMb}
            />
          )}

          {!responseMessage && !checkConfirmRequestError && this.props.unblindingRequestCreatePermission && (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    disabled={hasErrors(getFieldsError())}
                    className={classes.submitButton}
                  >
                    {this.props.i18n.t('NamespacePatientRegistryForm:Request unblinding')}
                    {isLoading && <Icon type="loading" />}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          )}
        </Form>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userToken: getUserToken(state),
  unblindingRequestCreatePermission: getUnblindingRequestCreatePermission(state),
});

const mapDispatchToProps = {
  resetPassword,
  loginTwoFactor,
};

export default Form.create({ name: "patientRegistryForm" })(
  connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(PatientRegistryForm))
);



// WEBPACK FOOTER //
// ./src/components/PatientProfileForm/PatientRegistryForm.js